import { EventEmitter, Injectable, Optional } from '@angular/core';
import { ActivityRemoteControllerService, PdfDocumentRemoteControllerService, ProcessRemoteControllerService, ReportRemoteControllerService } from 'core/api';
import { ConfigService } from 'core/config';
import { ChecklistDialogService, DynamicFormDialogService } from 'core/dialogs';
import { EProcessSharing, EProcessStatus, IActivityDto, IProcessDto } from 'core/dto';
import { IControl, IDateMinRule } from 'core/dynamic-form';
import { filterAllTruthyInTuple, mapKey, subjectFromObs } from 'core/rx-js';
import { AppRouterService, LanguageService, LoaderService, MessageBoxService, SnackbarService } from 'core/services';
import { dateUtils, fileUtils } from 'core/utils';
import { BehaviorSubject, MonoTypeOperatorFunction, Observable, Subject, combineLatest, distinctUntilChanged, filter, map, of, switchMap, take, takeUntil, tap } from 'rxjs';
import { UserService } from '../../../global-services/user.service';
import { DsgDeepTranslationKey } from '../../../pipes/translate.pipe';
import { ProcessOverviewService } from '../process-overview.service';

export type LProcessAction = 'delete' | 'cancel' | 'reject' | 'download' | 'download-audit-trail' | 'restart' | 'change-valid-till'


@Injectable()
export class ProcessActionsService {
  private readonly _destroy = new Subject<void>()

  get isAdmin() {
    return this._userService.isAdmin
  }

  readonly processSubject = new BehaviorSubject<IProcessDto>(null)
  get process() {
    return this.processSubject.value
  }
  readonly userSubject = this._userService.userSubject;

  readonly isCreatorSubject = subjectFromObs({
    initialValue: false,
    observable: combineLatest([
      this.userSubject,
      this.processSubject
    ])
      .pipe(
        filterAllTruthyInTuple(),
        map(([user, process]) => user.email === process.creator.email)
      ),
    until: this._destroy
  })



  readonly creatorOrTeamMemberSubject = subjectFromObs({
    initialValue: false,
    observable: combineLatest([this.isCreatorSubject, this.processSubject]).pipe(
      map(([isCreator, process]) =>
        isCreator
        || (process?.userIsTeamMember && process?.processSharingType === EProcessSharing.creatorSignersTeams)
      ),
      distinctUntilChanged()
    ),
    until: this._destroy
  })




  readonly canDeleteSubject = subjectFromObs({
    initialValue: false,
    observable: combineLatest([
      this.creatorOrTeamMemberSubject,
      this.processSubject
    ])
      .pipe(
        map(([creatorOrTeamMember, process]) => creatorOrTeamMember && process?.status === EProcessStatus.Draft),
        distinctUntilChanged()
      ),

    until: this._destroy
  })



  readonly canSignSubject = subjectFromObs({
    initialValue: false,
    observable: this.processSubject.pipe(map(process => process?.status === EProcessStatus.WaitingForMe)),
    until: this._destroy
  })
  readonly canRejectSubject = subjectFromObs({
    initialValue: false,
    observable: this.processSubject.pipe(map(process => process?.status === EProcessStatus.WaitingForMe)),
    until: this._destroy
  })

  readonly canDownloadSubject = subjectFromObs({
    initialValue: false,
    observable: this.processSubject.pipe(
      filter(Boolean),
      filter((process) => process.status === EProcessStatus.Finished),
      mapKey('id'),
      distinctUntilChanged(),
      switchMap(processId => this._pdfDocumentRemoteControllerService.canDownloadByProcessId(processId)),
      mapKey('canDownload')
    ),
    until: this._destroy
  })

  readonly canDownloadAuditTrailSubject = subjectFromObs({
    observable: combineLatest([
      this.userSubject,
      this.creatorOrTeamMemberSubject,
      this.processSubject
    ])
      .pipe(

        map(([user, creatorOrTeamMember, process]) =>
          (this.isAdmin || creatorOrTeamMember)
          && process?.status === EProcessStatus.Finished
        ),

      ),
    initialValue: false,
    until: this._destroy
  })



  readonly canRestartSubject = subjectFromObs({
    observable: combineLatest([this.processSubject, this.creatorOrTeamMemberSubject]).pipe(
      filter(([process]) => !!process),
      map(([process, creatorOrTeamMember]) => process.status === EProcessStatus.Expired && creatorOrTeamMember)
    ),
    initialValue: false,
    until: this._destroy
  })

  readonly canCancelSubject = subjectFromObs({
    initialValue: false,
    until: this._destroy,
    observable: combineLatest([
      this.processSubject,
      this.creatorOrTeamMemberSubject
    ]).pipe(
      map(([process, creatorOrTeamMember]) =>
        [EProcessStatus.InProgress, EProcessStatus.WaitingForMe, EProcessStatus.WaitingForOthers].includes(process?.status)
        && creatorOrTeamMember
      ),
    )
  })

  readonly canChangeValidTillSubject = subjectFromObs({
    initialValue: false,
    observable: combineLatest([
      this.processSubject,
      this.creatorOrTeamMemberSubject
    ])
      .pipe(
        map(([process, creatorOrTeamMember]) =>
          [EProcessStatus.WaitingForMe, EProcessStatus.WaitingForOthers, EProcessStatus.InProgress].includes(process?.status)
          &&
          (this.isAdmin || creatorOrTeamMember)
        ),
        distinctUntilChanged()
      ),
    until: this._destroy
  })

  readonly noMenuActionsSubject = subjectFromObs({
    initialValue: true,
    until: this._destroy,
    observable: combineLatest([
      this.canDeleteSubject,
      this.canRejectSubject,
      this.canCancelSubject,
      this.canDownloadAuditTrailSubject,
      this.canDownloadSubject,
      this.canRestartSubject,
      this.canChangeValidTillSubject
    ])
      .pipe(
        map(actions => !actions.find(Boolean)),
      )
  })



  readonly activitySubject = new BehaviorSubject<IActivityDto>(null);
  get activity() {
    return this.activitySubject.value
  }

  readonly actionCompleted = new EventEmitter<LProcessAction>()
  readonly emitAction = <T extends any>(action: LProcessAction) => tap(() => this.actionCompleted.emit(action)) as MonoTypeOperatorFunction<T>;

  readonly executeActionSideffects = <T extends any>(params: { id: string, errorMessage: DsgDeepTranslationKey, succesMessage: DsgDeepTranslationKey, action: LProcessAction }) => (obs: Observable<T>) =>
    obs.pipe(
      take(1),
      this.emitAction(params.action),
      this._loaderService.disposeLoader(params.id),
      this._snackbarService.handleAsync({ errorMessage: params.errorMessage, successMessage: params.succesMessage }),
    )

  get language() {
    return this._languageService.selectedLanguage;
  }

  constructor(
    private readonly _userService: UserService,
    private readonly _activityRemoteControllerService: ActivityRemoteControllerService,
    private readonly _processRemoteControllerService: ProcessRemoteControllerService,
    private readonly _loaderService: LoaderService<DsgDeepTranslationKey>,
    private readonly _snackbarService: SnackbarService<DsgDeepTranslationKey>,
    private readonly _pdfDocumentRemoteControllerService: PdfDocumentRemoteControllerService,
    private readonly _checklistDialogService: ChecklistDialogService,
    private readonly _messageBoxService: MessageBoxService<DsgDeepTranslationKey>,
    private readonly _dynamicFormDialogService: DynamicFormDialogService,
    private readonly _appRouterService: AppRouterService,
    private readonly _reportRemoteControllerService: ReportRemoteControllerService,
    private readonly _languageService: LanguageService,
    private readonly _configService: ConfigService,
    @Optional()
    private readonly _processOverviewService: ProcessOverviewService
  ) {


    this.actionCompleted.pipe(
      takeUntil(this._destroy),
      filter((action) => action === 'cancel' || action === 'reject' || action === 'change-valid-till' || action === 'restart'),
      switchMap(() => {

        const id = this._loaderService.show({ key: 'process.load-one', text: 'ss-entities.process.remote-actions.by-key' });

        return this._processRemoteControllerService.getOne(this.process?.id)
          .pipe(
            this._loaderService.disposeLoader(id),
            take(1),

          )
      })
    ).subscribe((process) => {
      this.processSubject.next(process);
    })

    this.processSubject.pipe(
      filter(Boolean),
      filter((process) => process.status === EProcessStatus.WaitingForMe),
      switchMap((process) => this._activityRemoteControllerService.getForUserByProcessId(process.id)),
      takeUntil(this._destroy)
    )
      .subscribe((activity) => {
        this.activitySubject.next(activity);
      })
  }

  reject() {


    this._dynamicFormDialogService.open({
      controls: [{ dataField: 'reason', label: 'ss-entities.activity.reject.reason', type: 'textarea', validationRules: [{ type: 'required' }] }],
      subtitle: '',
      title: 'ss-entities.activity.reject.title',
      defaultBtnText: 'ss-entities.activity.reject.display',
    })
      .pipe(
        take(1),
        filter(Boolean),
        switchMap((formData) => {
          const id = this._loaderService.show({ key: 'activity.reject.' + this.process.id, text: 'ss-entities.activity.reject.pending' })

          return this._activityRemoteControllerService.reject(this.activity.id, formData.reason)
            .pipe(
              this.executeActionSideffects({ id, errorMessage: 'ss-entities.activity.reject.error', succesMessage: 'ss-entities.activity.reject.success', action: 'reject' }),
            )
        }),
        take(1)
      )
      .subscribe()

  }

  cancel() {


    this._messageBoxService.show({ buttons: 'YesNo', title: 'ss-entities.process.actions.cancel.title', text: 'ss-entities.process.actions.cancel.prompt', type: 'prompt' })
      .pipe(
        take(1),
        filter((answer) => answer === 'Yes'),
        switchMap(() => {
          const id = this._loaderService.show({ key: 'process.cancel.' + this.process.id, text: 'ss-entities.process.actions.cancel.pending' })
          return this._processRemoteControllerService.cancel(this.process.id)
            .pipe(
              this.executeActionSideffects({ id, errorMessage: 'ss-entities.process.actions.cancel.error', succesMessage: 'ss-entities.process.actions.cancel.success', action: 'cancel' }),
            )
        }),
        take(1)
      ).subscribe()
  }

  deleteProcess() {

    this._messageBoxService.show({
      buttons: 'YesNo',
      title: 'ss-entities.process.actions.delete.title',
      text: 'ss-entities.process.actions.delete.text',
      type: 'prompt',
      replace: {
        '@display': this.process.description
      }
    })
      .pipe(
        take(1),
        filter((answer) => answer === 'Yes'),
        switchMap(() => {
          const id = this._loaderService.show({ key: 'process.delete.' + this.process.id, text: 'ss-entities.process.actions.delete.pending' })

          return this._processRemoteControllerService.remove(this.process.id)
            .pipe(
              this.executeActionSideffects({ id, errorMessage: 'ss-entities.process.actions.delete.error', succesMessage: 'ss-entities.process.actions.delete.success', action: 'delete' }),
            )
        }),
        take(1)
      )
      .subscribe()

  }

  downloadDocuments() {

    this._pdfDocumentRemoteControllerService.getByProcess(this.process.id).pipe(
      switchMap((docs) => {
        const id = this._loaderService.show({ key: 'process.download-documents.' + this.process.id, text: 'ss-entities.document.remote-actions.load' })
        return this._pdfDocumentRemoteControllerService.downloadDocuments(docs.map(({ id }) => id))
          .pipe(
            this._loaderService.disposeLoader(id),
          )
      }),
      this._snackbarService.handleError({
        type: 'error',
        getErrorMessage: (errorResponse) => errorResponse.error.error?.status.message || 'ss-general.error'
      }),

      switchMap((documents) => this._checklistDialogService.open({
        data: documents,
        title: 'document-download.title',
        displayExpr: 'name',
        valueExpr: 'id',
        defaultBtnText: 'ss-general.download',
        defaultCheckAll: true
      })),
      take(1),
      filter(Boolean),
      this.emitAction('download')
    )
      .subscribe(async (documents) => {

        for (const doc of documents) {
          await fileUtils.downloadPdf(doc.content, doc.name)
        }
      })

  }

  downloadAuditTrail() {

    const id = this._loaderService.show({ key: 'process.download-audit-trail.' + this.process.id, text: 'ss-entities.process.actions.audit-trail.pending' })

    this._reportRemoteControllerService.getProcessAuditTrail(this.process.id, this.language)
      .pipe(
        this.executeActionSideffects({
          id,
          errorMessage: 'ss-entities.process.actions.audit-trail.error',
          succesMessage: 'ss-entities.process.actions.audit-trail.display',
          action: 'download-audit-trail'
        }),
        take(1)
      )
      .subscribe(({ name, content }) => {

        fileUtils.downloadPdf(content, name);
      })


  }

  sign() {

    this._appRouterService.navigateToViewer(this.activity.id, true);
  }


  restart() {

    this._dynamicFormDialogService.open<{ validTill: Date }>({
      title: 'ss-entities.process.actions.restart.title' as DsgDeepTranslationKey,
      subtitle: 'ss-entities.process.actions.restart.subtitle' as DsgDeepTranslationKey,
      defaultBtnText: 'ss-entities.process.actions.restart.display',
      controls: [
        {
          dataField: 'validTill',
          label: 'ss-entities.process.valid-until',
          type: 'date',
          options: { min: new Date() },
          defaultValue: dateUtils.add('day', new Date(), this._configService.appConfig.processExpirationInDaysDefault),
          validationRules: [
            { min: new Date(), type: 'dateMin' } as IDateMinRule
          ]
        } as IControl<'date'>
      ]
    })
      .pipe(
        take(1),
        switchMap((formData) => {
          if (!formData?.validTill) {
            return of(null);
          }

          const id = this._loaderService.show({ key: 'process.restart.' + this.process.id, text: 'ss-entities.process.actions.restart.pending' })

          return this._processRemoteControllerService.extendValidity(this.process.id, formData.validTill).pipe(
            this.executeActionSideffects({
              id,
              errorMessage: 'ss-entities.process.actions.restart.error',
              succesMessage: 'ss-entities.process.actions.restart.success',
              action: 'restart'
            })
          )
        }),
        take(1)
      )
      .subscribe()
  }


  changeValidTill() {

    this._dynamicFormDialogService.open<{ validTill: Date }>({
      title: 'ss-entities.process.actions.change-valid-till.title' as DsgDeepTranslationKey,
      subtitle: 'ss-entities.process.actions.change-valid-till.subtitle' as DsgDeepTranslationKey,
      defaultBtnText: 'ss-entities.process.actions.change-valid-till.display',
      controls: [
        {
          dataField: 'validTill',
          label: 'ss-entities.process.valid-until',
          type: 'date',
          options: { min: new Date() },
          defaultValue: this.process.validTill || dateUtils.add('day', new Date(), this._configService.appConfig.processExpirationInDaysDefault),
          validationRules: [
            { min: new Date(), type: 'dateMin' } as IDateMinRule
          ]
        } as IControl<'date'>
      ]
    })
      .pipe(
        take(1),
        switchMap((formData) => {
          if (!formData?.validTill) {
            return of(null);
          }

          const id = this._loaderService.show({ key: 'process.change-valid-till.' + this.process.id, text: 'ss-entities.process.actions.change-valid-till.pending' })

          return this._processRemoteControllerService.extendValidity(this.process.id, formData.validTill).pipe(
            this.executeActionSideffects({
              id,
              errorMessage: 'ss-entities.process.actions.change-valid-till.error',
              succesMessage: 'ss-entities.process.actions.change-valid-till.success',
              action: 'change-valid-till'
            })
          )
        }),
        take(1)
      )
      .subscribe()
  }

  ngOnDestroy() {
    this._destroy.next()
    this._destroy.complete()
  }

}
